import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { BreadcrumbComponent } from '@merchant-portal/app/components/breadcrumb/breadcrumb.component';
import { SvgLoaderModule } from '@merchant-portal/app/components/icon/svg-loader/svg-loader/svg-loader.module';
import { BlockLoadingComponent } from '@merchant-portal/app/components/loading/block-loading/block-loading.component';
import {
  GTMEventCategory,
  GTMEventType,
  GTMServiceFactory,
  GtmTrackClickDirective,
} from '@merchant-portal/app/core/google-tag-manager';
import { PaymentOption } from '@merchant-portal/app/models/disbursement/disbursement.interface';
import { TopUpInstruction } from '@merchant-portal/app/models/wallet/merchant-history/top-up-instruction.interface';
import { SafeUrlService } from '@merchant-portal/app/services/safe-url.service';
import { UtilService } from '@merchant-portal/app/services/util.service';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DisbursementService } from '../services/disbursement.service';
import { TopUpInstructionService } from './services/top-up-instruction.service';

@Component({
  selector: 'app-payment-method-selection',
  templateUrl: './payment-method-selection.component.html',
  styleUrls: ['./payment-method-selection.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    BlockLoadingComponent,
    SvgLoaderModule,
    ButtonModule,
    RadioButtonModule,
    AccordionModule,
    GtmTrackClickDirective,
  ],
})
export class PaymentMethodSelectionComponent implements OnInit {
  private disbursementService = inject(DisbursementService);
  private topUpInstructionService = inject(TopUpInstructionService);
  private safeUrlService = inject(SafeUrlService);
  private utilService = inject(UtilService);
  private gtmService = inject(GTMServiceFactory);

  readonly GTMEventType = GTMEventType;
  readonly GTMEventCategory = GTMEventCategory;

  paymentMethods: PaymentOption[] = [];
  isLoading = false;
  selectedBank: PaymentOption | null = null;
  showInstructions = false;
  topUpInstruction: TopUpInstruction | null = null;
  loadingLogos: { [logoUrl: string]: boolean } = {};
  private sanitizedUrls = new Map<string, SafeUrl>();

  ngOnInit() {
    this.loadPaymentMethods();
  }

  loadPaymentMethods() {
    this.isLoading = true;
    this.disbursementService.getPaymentMethods('merchant_top_up').subscribe({
      next: response => {
        this.paymentMethods = [
          ...(response.data.virtualAccount || []),
          ...(response.data.bankTransfer || []),
          ...(response.data.creditCard || []),
        ];

        this.paymentMethods.forEach(bank => {
          if (this.isExternalLogoUrl(bank.logo) && bank.logo) {
            this.sanitizedUrls.set(bank.logo, this.safeUrlService.sanitizeUrl(bank.logo));
            this.loadingLogos[bank.logo] = true;
          }
        });

        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  viewTopUpInstructions() {
    if (this.selectedBank) {
      this.isLoading = true;
      if (this.isExternalLogoUrl(this.selectedBank.logo) && this.selectedBank.logo) {
        if (!this.sanitizedUrls.has(this.selectedBank.logo)) {
          this.sanitizedUrls.set(
            this.selectedBank.logo,
            this.safeUrlService.sanitizeUrl(this.selectedBank.logo)
          );
        }
        this.loadingLogos[this.selectedBank.logo] = true;
      }

      const context = location.pathname.includes('/wallet') ? 'wallet' : 'disbursement';

      this.topUpInstructionService
        .getTopUpInstructions(this.selectedBank.uuid, this.selectedBank, context)
        .subscribe({
          next: instruction => {
            this.topUpInstruction = instruction;
            this.showInstructions = true;
            this.isLoading = false;

            this.gtmService.trackCustomEvent(GTMEventType.WAITING_FOR_TOP_UP_PAGE_VIEWED, {
              category: GTMEventCategory.PAGE,
              additionalData: {
                payment_method: this.selectedBank?.name,
                bank_uuid: this.selectedBank?.uuid,
                context: context,
              },
            });
          },
          error: () => {
            this.isLoading = false;
          },
        });
    }
  }

  onBack() {
    this.showInstructions = false;
    this.topUpInstruction = null;
  }

  onBankChange(bank: any) {
    console.log(bank);
  }

  copyVirtualAccount() {
    if (this.topUpInstruction?.virtualAccountNumber) {
      navigator.clipboard.writeText(this.topUpInstruction.virtualAccountNumber);
      this.gtmEventTrack(GTMEventType.VIRTUAL_ACCOUNT_COPIED);
      this.gtmEventTrack(GTMEventType.TOP_UP_BALANCE_PAGE_SUCCESSFULLY);
    }
  }

  isExternalLogoUrl(logo?: string): boolean {
    return this.utilService.isExternalLogoUrl(logo);
  }

  getSvgLogoName(logo?: string): string {
    if (logo) {
      const fileName = logo.includes('/') ? logo.split('/').pop() || logo : logo;
      return `full-${fileName}`;
    }
    return '';
  }

  getExternalLogoUrl(logo?: string): SafeUrl {
    if (!logo) return this.safeUrlService.sanitizeUrl('');
    return this.sanitizedUrls.get(logo) || this.safeUrlService.sanitizeUrl('');
  }

  onLogoLoad(url: string) {
    this.loadingLogos[url] = false;
  }

  onLogoError(url: string) {
    this.loadingLogos[url] = false;
  }

  isLogoLoading(url: string): boolean {
    return this.loadingLogos[url] !== false;
  }

  gtmEventTrack(event: GTMEventType) {
    this.gtmService.trackCustomEvent(event, {
      category: GTMEventCategory.BUTTON,
      action: 'copy',
      label: 'Virtual Account Number',
      additionalData: {
        payment_method: this.selectedBank?.name,
        bank_uuid: this.selectedBank?.uuid,
        virtual_account_number: this.topUpInstruction?.virtualAccountNumber,
      },
    });
  }
}
